/**
 * Self-service (HF2, H8 `/employee/interplay/*`): when the signed-in person was synced from a host
 * product, their name, work email, phone, job title and unit are kept there. This says so, offers
 * "Propose a change" (sent to the host, which applies it in its own records) and shows how many of
 * their proposals are waiting. Nothing renders while off or not synced.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Stack } from '@mui/material';
import type { Ownership } from '@/api/endpoints/interplayPeople';
import ThemeScope from '@/pages/payScales/ThemeScope';
import { useHostBrands } from './hooks';
import ProposeChangeDialog, { type ProposeCurrent } from './ProposeChangeDialog';
import SyncedChip from './SyncedChip';
import { ownersLabel } from './model';

export default function EssSyncedNotice({ own, current }: { own: Ownership; current: ProposeCurrent }) {
  const { t } = useTranslation();
  const brands = useHostBrands();
  const [open, setOpen] = useState(false);
  const host = ownersLabel(t, own.owners, brands);
  return (
    <ThemeScope>
      <Alert
        severity="info"
        icon={false}
        sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
        data-testid="ess-synced-notice"
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} justifyContent="space-between">
          <Stack spacing={0.75}>
            <SyncedChip owners={own.owners} brands={brands} />
            <span>{t('interplay_people.ess.body', { host })}</span>
            {own.pending_proposals > 0 && (
              <strong data-testid="ess-pending">{t('interplay_people.ess.pending', { count: own.pending_proposals, host })}</strong>
            )}
          </Stack>
          <Button size="small" variant="outlined" onClick={() => setOpen(true)} sx={{ flex: 'none' }}>
            {t('interplay_people.propose.button_ess')}
          </Button>
        </Stack>
      </Alert>
      {open && (
        <ProposeChangeDialog open onClose={() => setOpen(false)} mode="ess" current={current} owners={own.owners} brands={brands} />
      )}
    </ThemeScope>
  );
}
